import React from 'react';
import {
  TableCellsIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  ScaleIcon,
  DocumentTextIcon,
  StarIcon,
} from '@heroicons/react/24/outline';

const odagTables = [
  {
    label: 'Universe Table 1',
    short: 'OD',
    description: 'Standard and Expedited Pre-service Organization Determinations',
    universeType: 'odag_table1',
    gradient: 'from-orange-500 to-amber-500',
    icon: TableCellsIcon,
    color: 'blue'
  },
  {
    label: 'Universe Table 2',
    short: 'RECON',
    description: 'Standard and Expedited Pre-service Reconsiderations',
    universeType: 'odag_table2_reconsiderations',
    gradient: 'from-emerald-500 to-teal-500',
    icon: ArrowPathIcon,
    color: 'emerald'
  },
  {
    label: 'Universe Table 3',
    short: 'PYMT_C',
    description: 'Payment Organization Determinations and Reconsiderations',
    universeType: 'odag_table3_pymt_c',
    gradient: 'from-purple-500 to-amber-500',
    icon: CurrencyDollarIcon,
    color: 'purple'
  },
  {
    label: 'Universe Table 4',
    short: 'EFF_C',
    description: 'Effectuations of Overturned Decisions by IRE, ALJ, or MAC',
    universeType: 'odag_table4_eff_c',
    gradient: 'from-orange-500 to-red-500',
    icon: ScaleIcon,
    color: 'orange'
  },
  {
    label: 'Universe Table 5',
    short: 'GRV_C',
    description: 'Part C Standard and Expedited Grievances',
    universeType: 'odag_table5_grv_c',
    gradient: 'from-pink-500 to-rose-500',
    icon: DocumentTextIcon,
    color: 'pink'
  },
  {
    label: 'Universe Table 6',
    short: 'AIP',
    description: 'Dual SNP Reductions, Suspensions, Terminations',
    universeType: 'odag_table6_aip',
    gradient: 'from-violet-500 to-amber-500',
    icon: StarIcon,
    color: 'violet'
  },
];

export default function ODAGTableSelector({ onSelect }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-amber-500/3 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen p-6">
        <div className="w-full max-w-8xl mx-auto">
          {/* Section badge */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/90 backdrop-blur-2xl border border-slate-200/60 rounded-2xl shadow-2xl">
              <div className="w-3 h-3 bg-gradient-to-r from-orange-500 via-purple-500 to-pink-500 rounded-full animate-pulse shadow-lg shadow-orange-500/50"></div>
              <span className="text-sm font-bold text-slate-700 tracking-wider uppercase">ODAG Universe Tables</span>
            </div>
          </div>

          {/* Ultra-modern grid with advanced effects */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {odagTables.map((table, index) => {
              const Icon = table.icon;
              return (
                <button
                  key={index}
                  onClick={() => onSelect(table)}
                  className="
                    group relative w-full text-left
                    bg-white/90 backdrop-blur-2xl
                    rounded-3xl p-10
                    border border-slate-200/60
                    shadow-2xl shadow-slate-200/50
                    hover:shadow-3xl hover:shadow-slate-300/60
                    hover:-translate-y-4 hover:scale-[1.03]
                    transition-all duration-700 ease-out
                    focus:outline-none focus:ring-4 focus:ring-orange-500/30 focus:ring-offset-4 focus:ring-offset-white
                    overflow-hidden
                    before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/40 before:to-transparent before:opacity-0 before:group-hover:opacity-100 before:transition-opacity before:duration-700
                  "
                >
                  {/* Dynamic background gradient */}
                  <div className={`
                    absolute inset-0 bg-gradient-to-br ${table.gradient} 
                    opacity-0 group-hover:opacity-10 
                    transition-opacity duration-700
                  `} />
                  
                  {/* Animated border glow */}
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-orange-500/20 via-purple-500/20 to-pink-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-sm"></div>
                  
                  {/* Content */}
                  <div className="relative z-10">
                    {/* Header with icon and badges */}
                    <div className="flex items-start justify-between mb-8">
                      <div className={`
                        h-20 w-20 rounded-3xl bg-gradient-to-br ${table.gradient}
                        shadow-2xl shadow-black/20
                        flex items-center justify-center
                        group-hover:scale-110 group-hover:rotate-6
                        transition-all duration-700 ease-out
                        relative overflow-hidden
                        before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/30 before:to-transparent before:opacity-0 before:group-hover:opacity-100 before:transition-opacity before:duration-700
                      `}>
                        <Icon className="h-10 w-10 text-white relative z-10" />
                      </div>
                      
                      <div className="flex flex-col items-end gap-3">
                        <span className="px-4 py-2 bg-slate-100/90 backdrop-blur-xl text-slate-700 text-sm font-bold rounded-xl border border-slate-200/60 shadow-lg">
                          {table.short}
                        </span>
                        <span className="px-3 py-1.5 bg-gradient-to-r from-orange-100/90 to-purple-100/90 backdrop-blur-xl text-orange-700 text-xs font-semibold rounded-lg border border-orange-200/60">
                          Universe
                        </span>
                      </div>
                    </div>

                    {/* Title and description */}
                    <div className="mb-8">
                      <h3 className="text-2xl font-black text-slate-900 mb-4 leading-tight group-hover:text-orange-800 transition-colors duration-500">
                        {table.label}
                      </h3>
                      <p className="text-slate-600 leading-relaxed text-base group-hover:text-slate-700 transition-colors duration-500 font-light">
                        {table.description}
                      </p>
                    </div>

                    {/* Action footer */}
                    <div className="flex items-center justify-between pt-6 border-t border-slate-200/60">
                      <span className="text-orange-600 font-bold text-base group-hover:text-orange-700 transition-colors duration-500">
                        Select Table
                      </span>
                      <div className="w-12 h-12 rounded-2xl bg-slate-100/90 backdrop-blur-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:scale-110 border border-slate-200/60">
                        <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Subtle inner glow */}
                  <div className="absolute inset-0 rounded-3xl ring-1 ring-slate-200/60 group-hover:ring-orange-300/60 pointer-events-none transition-all duration-700" />
                </button>
              );
            })}
          </div>

          {/* Bottom spacing */}
          <div className="h-16"></div>
        </div>
      </div>
    </div>
  );
}
